import { useState } from "react";
import { Crown, Headphones, ShieldCheck, Sparkles, X } from "lucide-react";

const AGENT_PROFILES = [
  {
    name: "DRACO",
    title: "Game host",
    detail: "Fast account and game help",
    initial: "D",
    avatarClass: "from-[#4c1010] via-[#9f2b1f] to-[#d4af37]",
    image: "/assets/agents/draco.png",
    Icon: Crown,
  },
  {
    name: "SOPHIA",
    title: "Support lead",
    detail: "Wallet and reward help",
    initial: "S",
    avatarClass: "from-[#06311f] via-[#0f6f48] to-[#d4af37]",
    image: "/assets/agents/sophia.png",
    Icon: ShieldCheck,
  },
  {
    name: "AURUM",
    title: "VIP desk",
    detail: "Redeem and load help",
    initial: "A",
    avatarClass: "from-[#161047] via-[#5b2291] to-[#d4af37]",
    image: "/assets/agents/aurum.png",
    Icon: Sparkles,
  },
];

export function getAgentProfile(agent) {
  const name = typeof agent === "string" ? agent : agent?.name;
  if (!name) return null;
  return AGENT_PROFILES.find((profile) => profile.name === String(name).toUpperCase()) || null;
}

export default function SupportAgentPicker({ selectedAgent, onPick, className = "" }) {
  const [open, setOpen] = useState(false);
  const selected = getAgentProfile(selectedAgent);

  const choose = (profile) => {
    onPick(profile);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between gap-3 rounded-2xl border border-[#D4AF37]/25 bg-black/25 px-3 py-3 text-left hover:border-[#D4AF37]/60 hover:bg-[#D4AF37]/10"
      >
        <div className="flex min-w-0 items-center gap-3">
          {selected ? (
            <AgentAvatar profile={selected} />
          ) : (
            <div className="flex -space-x-3">
              {AGENT_PROFILES.map((profile) => (
                <AgentAvatar key={profile.name} profile={profile} compact />
              ))}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#F1C40F]">Your best agent</div>
            <div className="truncate text-sm font-bold text-white">
              {selected ? `Chatting with ${selected.name}` : "Choose DRACO, SOPHIA, or AURUM"}
            </div>
          </div>
        </div>
        <Headphones className="h-4 w-4 shrink-0 text-[#D4AF37]" />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close agent picker"
            className="fixed inset-0 z-[70] cursor-default bg-transparent"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 right-0 top-full z-[90] mt-2 rounded-2xl border border-[#D4AF37]/35 bg-[#07100b] p-3 shadow-2xl shadow-black/70">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#F1C40F]">Pick your agent</div>
                <div className="text-xs text-white/50">Choose who you want to message.</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1.5 text-white/60 hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {AGENT_PROFILES.map((profile) => {
                const active = selected?.name === profile.name;
                return (
                  <button
                    key={profile.name}
                    type="button"
                    onClick={() => choose(profile)}
                    className={`rounded-2xl border px-2 py-3 text-center transition ${
                      active
                        ? "border-[#D4AF37] bg-[#D4AF37]/15"
                        : "border-white/10 bg-white/[0.04] hover:border-[#D4AF37]/60 hover:bg-[#D4AF37]/10"
                    }`}
                  >
                    <AgentAvatar profile={profile} large />
                    <div className="mt-2 text-xs font-black text-white">{profile.name}</div>
                    <div className="mt-0.5 text-[10px] leading-tight text-white/45">{profile.title}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AgentAvatar({ profile, compact = false, large = false }) {
  const Icon = profile.Icon;
  const size = large ? "h-14 w-14" : compact ? "h-9 w-9" : "h-11 w-11";
  const iconSize = large ? "h-4 w-4" : "h-3.5 w-3.5";
  return (
    <div className={`relative mx-auto ${size} shrink-0 overflow-hidden rounded-full border border-[#D4AF37]/40 bg-gradient-to-br ${profile.avatarClass} p-[2px] shadow-lg shadow-black/30`}>
      <div className="grid h-full w-full place-items-center overflow-hidden rounded-full bg-[#07100b]">
        {profile.image ? (
          <img src={profile.image} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="font-display text-lg font-black text-[#F1C40F]">{profile.initial}</span>
        )}
      </div>
      <span className="absolute bottom-0 right-0 grid h-5 w-5 place-items-center rounded-full border border-[#07100b] bg-[#D4AF37] text-black">
        <Icon className={iconSize} />
      </span>
    </div>
  );
}
